import CommercialLayout from "../../../components/CommercialLayout";
import { useStores } from "../../../context/storesContext";
import {
  BigQueryComparative,
  TurnoverObjective,
} from "../../../types/commercial";
import CommercialContentPage from "../../../components/CommercialContentPage";
import { fetcherWithStoreKey } from "../../../utils/fetcher";
import useSWR from "swr";
import { Range } from "../../../utils/range";
import { EcomServiceType } from "../../../utils/ecomServiceType";
import { useKpiVisibility } from "../../../kpis/useKpiVisibility";
import useBreakPoint from "../../../utils/useBreakPoint";
import CommercialContentPageDesktop from "../../../components/CommercialContentPageDesktop";
import { useAuth } from "../../../Auth/AuthProvider";
import { isEmailInWhitelist } from "../../../utils/whiteListUtils";
import useLandscape from "../../../utils/useLandscape";

function Week() {
  const { user } = useAuth();
  const { selectedStore } = useStores();
  const { isKpiExists } = useKpiVisibility();

  const currentView = useBreakPoint();
  const isLandscape = useLandscape();
  const isDesktopOrTablet =
    currentView === "desktop" ||
    currentView === "tablet" ||
    (currentView === "phone" && isLandscape);
  const displayLegacyUi = !isEmailInWhitelist("ECOMMERCE_NEW_IHM_WHITE_LIST", user) || !isDesktopOrTablet;
  // Current week turnover
  const { data: weekTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "Turnover")
        ? [
            `/commercial/turnover?range=${Range.CurrentWeek}`,
            selectedStore?.storeKey as string, selectedStore.countryKey as string
          ]
        : null,
    ([url, storeEan, countryKey]) =>
      fetcherWithStoreKey(url,  storeEan as string, countryKey as string)
  );
  // past week turnover
  const { data: weekPastTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "Turnover")
        ? [
            `/commercial/turnover?range=${Range.CurrentWeekLastYear}`,
            selectedStore?.storeKey as string, selectedStore.countryKey as string
          ]
        : null,
    ([url, storeEan, countryKey]) =>
      fetcherWithStoreKey(url,  storeEan as string, countryKey as string)
  );
    // Week promo turnover
  const { data: weekTurnoverPromoData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverPromo")
        ? [
            `/commercial/turnoverPromo?range=${Range.CurrentWeek}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeKey]) =>
      fetcherWithStoreKey(url,  storeKey as string)
  );
  // Week member card turnover
  const { data: weekMemberCardTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "MemberCardTurnover")
        ? [
            `/commercial/memberCardTurnover?range=${Range.CurrentWeek}&chain=${"SDV"}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeKey]) =>
      fetcherWithStoreKey(url,  storeKey as string)
  );

  // Week past promo turnover
  const { data: weekPastTurnoverPromoData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverPromo")
        ? [
            `/commercial/turnoverPromo?range=${Range.CurrentWeekLastYear}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeKey]) =>
      fetcherWithStoreKey(url,  storeKey as string)
  );

  // Week past member card turnover
  const { data: weekPastMemberCardTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "MemberCardTurnover")
        ? [
            `/commercial/memberCardTurnover?range=${Range.CurrentWeekLastYear}&chain=${"SDV"}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeKey]) =>
      fetcherWithStoreKey(url,  storeKey as string)
  );

  //objective
  const { data: weekObjectiveTurnoverData } = useSWR<TurnoverObjective>(
    () =>
      selectedStore?.storeKey  && isKpiExists("Commercial", "Turnover")
        ? [
            `/commercial/objective?range=${Range.CurrentWeek}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // current Drive turnover
  const { data: currentWeekDriveTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/turnover?range=${Range.CurrentWeek}&ecomServiceType=${EcomServiceType.EcomTotal}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // past Drive turnover
  const { data: pastDriveTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/turnover?range=${Range.CurrentWeekLastYear}&ecomServiceType=${EcomServiceType.EcomTotal}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  //Comparative
  const { data: weekComparativeData, error: weekComparativeError } = useSWR<
    Array<BigQueryComparative>
  >(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "Comparative")
        ? [
            `/commercial/comparative?range=${
              Range.CurrentWeek
            }&storeType=${selectedStore?.storeType?.toUpperCase()}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // currentWeek sector turnover
  const { data: currentWeekSectorData, error: currentWeekSectorError } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverBySector") && displayLegacyUi
        ? [
            `/commercial/sector?range=${
              Range.CurrentWeek
            }&storeType=${selectedStore?.storeType.toUpperCase()}&sector=${"SDV"}`,
            selectedStore?.storeKey as string, selectedStore.countryKey as string
          ]
        : null,
    ([url, storeEan,  countryKey]) =>
      fetcherWithStoreKey(url,  storeEan as string,  countryKey as string)
  );
  // Objective DRIVE
  const { data: currentDriveObjectiveData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/objective?range=${Range.CurrentWeek}&ecomServiceType=${EcomServiceType.Drive}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // tryptich turnover
    const { data: currentTurnoverTriptychData } = useSWR(
    () =>
      selectedStore?.storeKey && isKpiExists("Commercial", "TurnoverTriptych")
        ? [
            `/commercial/turnoverTriptych/allBrands?range=${Range.CurrentWeek}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );

    const { data: pastTurnoverTriptychData } = useSWR(
    () =>
      selectedStore?.storeKey&& isKpiExists("Commercial", "TurnoverTriptych")
        ? [
            `/commercial/turnoverTriptych/allBrands?range=${Range.CurrentWeekLastYear}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );

  const contentPageProps = {
    hasTurnoverByHour: false,
    hasComparative: true,
    range: Range.CurrentWeek,
    storeType: selectedStore?.storeType!,
    currentTurnoverPromoData: weekTurnoverPromoData,
    currentMemberCardTurnoverData: weekMemberCardTurnoverData,
    currentDriveData: currentWeekDriveTurnoverData,
    currentTurnoverData: weekTurnoverData,
    driveObjective: currentDriveObjectiveData,
    pastDriveData: pastDriveTurnoverData,
    pastTurnoverData: weekPastTurnoverData,
    pastTurnoverPromoData: weekPastTurnoverPromoData,
    pastMemberCardTurnoverData: weekPastMemberCardTurnoverData,
    sectorListData: currentWeekSectorData,
    turnoverObjectiveData: weekObjectiveTurnoverData!,
    comparativeList: weekComparativeData,
    currentTurnoverTriptychData: currentTurnoverTriptychData,
    pastTurnoverTriptychData: pastTurnoverTriptychData,
    isLoadingSector: !currentWeekSectorData,
    isLoadingTurnover: false,
    isLoadingTurnoverByHour: !!currentWeekSectorError || currentWeekSectorData === null,
    isLoadingComparative: !weekComparativeData,
    hasErrorSector: false,
    hasErrorTurnover: false,
    hasErrorTurnoverByHour: false,
    hasErrorComparative: !!weekComparativeError || weekComparativeData === null,
    openInformationModal: () => null,
  };

  return (
    <CommercialLayout>
      {displayLegacyUi ? (
        <CommercialContentPage {...contentPageProps} />
      ) : (
        <CommercialContentPageDesktop {...contentPageProps} />
      )}
    </CommercialLayout>
  );
}

export default Week;
